import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Plan, PaymentsConfig, PlanQuote } from "../../types";
import { capturePaypalOrder, createPaypalOrder } from "../../utils/api";
import { loadPayPal, type PayPalButtons } from "../../utils/paypal";
import { useAppConfig } from "../../config/AppConfigContext";
import { Banner, Icon } from "../common/ui";

/**
 * Buying one plan with PayPal — web only (AccountView never opens this inside
 * Telegram, and the server refuses Telegram sessions anyway).
 *
 * The amount is set by the server from the plan; the buttons only start the
 * order and hand back PayPal's approval. Access is granted server-side when the
 * capture is verified — or by PayPal's webhook if this tab is closed first.
 */
type Phase = "ready" | "paying" | "done" | "pending";

function errorText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  const m = e instanceof Error ? e.message.match(/[A-Z][A-Z_]{5,}/) : null;
  const code = m ? m[0] : "";
  return t(`pay.err.${code}`, { defaultValue: t("pay.err.generic") });
}

export default function CheckoutSheet({
  plan, payments, token, onClose, onPaid, quote,
}: {
  plan: Plan;
  /** Server quote: renewal / upgrade with credit. Absent → list price. */
  quote?: PlanQuote;
  payments: PaymentsConfig;
  token: string;
  onClose: () => void;
  onPaid: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");
  const { config } = useAppConfig();
  const holder = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>("ready");
  const [sdkReady, setSdkReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const title = (isRu ? plan.title_ru : plan.title_en) || plan.title_ru;
  const price = Number(quote?.due ?? plan.price_usd).toFixed(2);
  const isUpgrade = quote?.kind === "upgrade" && Number(quote.credit) > 0;
  const blockTitle = (key: string) => {
    const b = (config.blocks ?? []).find((x) => x.key === key);
    return b ? (isRu ? b.title_ru : b.title_en) : key;
  };

  // Close on Escape, unless a payment is mid-flight.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && phase !== "paying") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, phase]);

  useEffect(() => {
    if (!payments.client_id) return;
    let buttons: PayPalButtons | null = null;
    let alive = true;
    loadPayPal(payments.client_id, payments.currency, i18n.language || "ru")
      .then((pp) => {
        if (!alive || !holder.current) return;
        buttons = pp.Buttons({
          style: { layout: "vertical", shape: "pill", label: "pay", height: 46 },
          createOrder: async () => {
            setError(null);
            const { order_id } = await createPaypalOrder(plan.id, token);
            return order_id;
          },
          onApprove: async ({ orderID }) => {
            setPhase("paying");
            try {
              await capturePaypalOrder(orderID, token);
              setPhase("done");
              onPaid();
            } catch (e) {
              const text = errorText(t, e);
              if (/PAYMENT_PENDING/.test(String(e))) { setPhase("pending"); return; }
              setPhase("ready");
              setError(text);
            }
          },
          onCancel: () => setError(null),
          onError: (e) => { setPhase("ready"); setError(errorText(t, e)); },
        });
        holder.current.innerHTML = "";
        return buttons.render(holder.current).then(() => { if (alive) setSdkReady(true); });
      })
      .catch(() => { if (alive) setError(t("pay.err.SDK")); });
    return () => { alive = false; buttons?.close().catch(() => {}); };
  }, [payments.client_id, payments.currency, i18n.language, plan.id, token, t, onPaid]);

  return (
    <div className="sheet-backdrop" role="presentation" onClick={() => phase !== "paying" && onClose()}>
      <div
        className="sheet glass glass-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="between" style={{ marginBottom: 14 }}>
          <p className="eyebrow" style={{ margin: 0 }}>{t("pay.eyebrow")}</p>
          <button className="icon-btn" onClick={onClose} disabled={phase === "paying"} aria-label={t("common.close", { defaultValue: "Close" })}
            style={{ width: 36, height: 36 }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        {phase === "done" ? (
          <div className="checkout-done">
            <span className="checkout-done__icon"><Icon name="checkCircle" size={30} /></span>
            <h2 className="t-title" id="checkout-title">{t("pay.doneTitle")}</h2>
            <p className="t-sm muted" style={{ margin: 0 }}>
              {payments.bot_username ? t("pay.doneTextTelegram", { plan: title }) : t("pay.doneText", { plan: title })}
            </p>
            {payments.bot_username ? (
              <a className="btn btn-primary btn-block" href={`https://t.me/${payments.bot_username}`} style={{ marginTop: 6, textDecoration: "none" }}>
                <Icon name="send" size={17} /> {t("pay.backToTelegram")}
              </a>
            ) : (
              <button className="btn btn-primary btn-block" onClick={onClose} style={{ marginTop: 6 }}>
                {t("pay.doneButton")}
              </button>
            )}
          </div>
        ) : phase === "pending" ? (
          <div className="checkout-done">
            <span className="checkout-done__icon is-pending"><Icon name="clock" size={30} /></span>
            <h2 className="t-title" id="checkout-title">{t("pay.pendingTitle")}</h2>
            <p className="t-sm muted" style={{ margin: 0 }}>{t("pay.pendingText")}</p>
            <button className="btn btn-glass btn-block" onClick={onClose} style={{ marginTop: 6 }}>
              {t("pay.doneButton")}
            </button>
          </div>
        ) : (
          <>
            <h2 className="t-title" id="checkout-title" style={{ margin: "0 0 4px" }}>{title}</h2>
            <div className="checkout-price">
              <span className="checkout-price__value">${price}</span>
              <span className="t-xs muted-2">{t("pay.oneTime")}</span>
              {config.pricing_notice?.enabled && <span className="launch-pill">{t("planCard.launchPill")}</span>}
            </div>
            {quote?.kind === "renew" && quote.list_price && (
              <div className="checkout-upgrade">
                <div className="between"><span>{t("pay.listPrice")}</span><span style={{ textDecoration: "line-through" }}>${Number(quote.list_price).toFixed(2)}</span></div>
                <div className="between is-credit"><span>{t("pay.yourPrice")}</span><span>${Number(quote.due).toFixed(2)}</span></div>
                <p className="t-xs muted-2" style={{ margin: "6px 0 0", lineHeight: 1.4 }}>{t("pay.priceKeptNote")}</p>
              </div>
            )}
            {quote?.kind === "step_up" && (
              <div className="checkout-upgrade">
                <p className="t-xs" style={{ margin: 0, lineHeight: 1.45 }}>{t("pay.stepUpNote")}</p>
              </div>
            )}
            {isUpgrade && (
              <div className="checkout-upgrade">
                <div className="between"><span>{t("pay.listPrice")}</span><span>${Number(quote!.price).toFixed(2)}</span></div>
                <div className="between is-credit"><span>{t("pay.creditUnused")}</span><span>−${Number(quote!.credit).toFixed(2)}</span></div>
                <p className="t-xs muted-2" style={{ margin: "6px 0 0", lineHeight: 1.4 }}>{t("pay.upgradeNote")}</p>
              </div>
            )}

            <p className="t-xs muted-2" style={{ fontWeight: 700, margin: "14px 0 6px" }}>{t("pay.unlocks")}</p>
            <ul className="checkout-grants">
              {(plan.grants ?? []).map((g) => (
                <li key={g.block}>
                  <Icon name="check" size={15} stroke={2.6} />
                  <span>{blockTitle(g.block)}</span>
                  <span className="muted-2">{t("pay.forDays", { count: g.days })}</span>
                </li>
              ))}
            </ul>

            {/* Card payment is the part people miss: PayPal's card button needs no account. */}
            <div className="pay-methods">
              <span className="pay-methods__item"><b>PayPal</b> {t("pay.methodWallet")}</span>
              <span className="pay-methods__item is-card"><Icon name="lock" size={13} /> <b>{t("pay.methodCard")}</b> {t("pay.methodCardNote")}</span>
            </div>

            {payments.environment === "sandbox" && (
              <div style={{ marginTop: 12 }}><Banner kind="info">{t("pay.sandbox")}</Banner></div>
            )}
            {error && <div style={{ marginTop: 12 }}><Banner kind="error">{error}</Banner></div>}

            <div className="checkout-buttons" aria-busy={!sdkReady || phase === "paying"}>
              {(!sdkReady || phase === "paying") && (
                <div className="checkout-buttons__wait">
                  <div className="spinner spinner-amber" />
                  {phase === "paying" && <span className="t-sm">{t("pay.processing")}</span>}
                </div>
              )}
              <div ref={holder} style={{ visibility: phase === "paying" ? "hidden" : "visible" }} />
            </div>
            <p className="t-xs muted-2" style={{ margin: "10px 0 0", textAlign: "center", lineHeight: 1.45 }}>
              {isRu ? t("pay.rubNote", { rub: Number(plan.price).toLocaleString("ru-RU", { maximumFractionDigits: 2 }) }) : t("pay.cardNote")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
